import { Component, OnInit, OnDestroy } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterModule, Router } from '@angular/router';
import { WebPageService } from '../../services/web-page.service';
import { PageProcessService } from '../../services/page-process.service';
import { WebPage, AuditResult } from '../../models';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatChipsModule,
    MatProgressBarModule,
    MatDividerModule,
    MatListModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    RouterModule,
  ],
  template: `
    <div class="dashboard-container">
      <div class="page-header">
        <div>
          <h1>Dashboard</h1>
          <p class="subtitle">Resumen de seguridad de tus aplicaciones web</p>
        </div>
        <button mat-raised-button color="primary" (click)="refresh()" [disabled]="loading">
          <mat-icon>refresh</mat-icon>
          Actualizar
        </button>
      </div>

      <!-- Loading state -->
      <div class="loading-state" *ngIf="loading">
        <mat-spinner diameter="40"></mat-spinner>
        <p>Cargando datos...</p>
      </div>

      <!-- Empty state -->
      <div class="empty-state" *ngIf="!loading && stats.totalPages === 0">
        <mat-icon class="empty-icon">language</mat-icon>
        <h2>No tienes páginas registradas</h2>
        <p>Registra tu primera página web para comenzar a auditar su seguridad.</p>
        <button mat-raised-button color="primary" (click)="navigateToPages()">
          <mat-icon>add</mat-icon>
          Registrar Página
        </button>
      </div>

      <!-- Stats Cards -->
      <div class="stats-grid" *ngIf="!loading && stats.totalPages > 0">
        <div class="stat-card">
          <div class="stat-icon primary">
            <mat-icon>language</mat-icon>
          </div>
          <div class="stat-value">{{ stats.totalPages }}</div>
          <div class="stat-label">Páginas Registradas</div>
        </div>

        <div class="stat-card">
          <div class="stat-icon success">
            <mat-icon>verified_user</mat-icon>
          </div>
          <div class="stat-value">{{ stats.avgSecurityScore }}%</div>
          <div class="stat-label">Score Promedio</div>
        </div>

        <div class="stat-card">
          <div class="stat-icon warning">
            <mat-icon>warning</mat-icon>
          </div>
          <div class="stat-value">{{ stats.pagesWithIssues }}</div>
          <div class="stat-label">Páginas con Alertas</div>
        </div>

        <div class="stat-card">
          <div class="stat-icon error">
            <mat-icon>bug_report</mat-icon>
          </div>
          <div class="stat-value">{{ stats.criticalVulnerabilities }}</div>
          <div class="stat-label">Vulnerabilidades Críticas</div>
        </div>

        <div class="stat-card">
          <div class="stat-icon info">
            <mat-icon>people</mat-icon>
          </div>
          <div class="stat-value">{{ stats.activeUsers }}/{{ stats.totalUsers }}</div>
          <div class="stat-label">Usuarios Activos</div>
        </div>

        <div class="stat-card">
          <div class="stat-icon pending">
            <mat-icon>schedule</mat-icon>
          </div>
          <div class="stat-value">{{ stats.pendingAudits }}</div>
          <div class="stat-label">Auditorías Pendientes</div>
        </div>
      </div>

      <!-- Main Content Grid -->
      <div class="content-grid">
        <!-- Recent Audits -->
        <mat-card class="audits-card">
          <mat-card-header>
            <mat-card-title>Auditorías Recientes</mat-card-title>
            <span class="card-subtitle">Últimas 5 auditorías ejecutadas</span>
          </mat-card-header>
          <mat-card-content>
            <table mat-table [dataSource]="recentAudits" class="audits-table">
              <ng-container matColumnDef="pageName">
                <th mat-header-cell *matHeaderCellDef>Página</th>
                <td mat-cell *matCellDef="let audit">
                  <div class="page-cell">
                    <mat-icon class="page-icon">language</mat-icon>
                    {{ audit.pageName }}
                  </div>
                </td>
              </ng-container>

              <ng-container matColumnDef="date">
                <th mat-header-cell *matHeaderCellDef>Fecha</th>
                <td mat-cell *matCellDef="let audit">{{ audit.date | date: 'dd/MM/yyyy' }}</td>
              </ng-container>

              <ng-container matColumnDef="score">
                <th mat-header-cell *matHeaderCellDef>Score</th>
                <td mat-cell *matCellDef="let audit">
                  <div class="score-cell">
                    <mat-progress-bar
                      mode="determinate"
                      [value]="audit.score"
                      [color]="getScoreColor(audit.score)"
                    >
                    </mat-progress-bar>
                    <span [class]="getScoreClass(audit.score)">{{ audit.score }}%</span>
                  </div>
                </td>
              </ng-container>

              <ng-container matColumnDef="vulnerabilities">
                <th mat-header-cell *matHeaderCellDef>Vulnerabilidades</th>
                <td mat-cell *matCellDef="let audit">
                  <div class="vuln-mini-badges">
                    <span class="mini-badge critical" *ngIf="audit.vulnCritical">{{
                      audit.vulnCritical
                    }}</span>
                    <span class="mini-badge high" *ngIf="audit.vulnHigh">{{ audit.vulnHigh }}</span>
                    <span class="mini-badge medium" *ngIf="audit.vulnMedium">{{
                      audit.vulnMedium
                    }}</span>
                    <span class="mini-badge low" *ngIf="audit.vulnLow">{{ audit.vulnLow }}</span>
                  </div>
                </td>
              </ng-container>

              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Estado</th>
                <td mat-cell *matCellDef="let audit">
                  <span class="status-badge" [ngClass]="audit.status">
                    {{ getStatusLabel(audit.status) }}
                  </span>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="auditColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: auditColumns"></tr>
            </table>
          </mat-card-content>
          <mat-card-actions>
            <button mat-button color="primary" (click)="navigateToPages()">Ver todas las auditorías</button>
          </mat-card-actions>
        </mat-card>

        <!-- Right Column -->
        <div class="right-column">
          <!-- Vulnerabilities Summary -->
          <mat-card class="vulnerabilities-card">
            <mat-card-header>
              <mat-card-title>Resumen de Vulnerabilidades</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="vuln-list">
                <div class="vuln-item critical">
                  <div class="vuln-info">
                    <span class="vuln-label">Críticas</span>
                    <span class="vuln-count">{{ totalVulnerabilities.critical }}</span>
                  </div>
                  <mat-progress-bar
                    mode="determinate"
                    [value]="getVulnPercentage('critical')"
                    color="warn"
                  ></mat-progress-bar>
                </div>

                <div class="vuln-item high">
                  <div class="vuln-info">
                    <span class="vuln-label">Altas</span>
                    <span class="vuln-count">{{ totalVulnerabilities.high }}</span>
                  </div>
                  <mat-progress-bar
                    mode="determinate"
                    [value]="getVulnPercentage('high')"
                  ></mat-progress-bar>
                </div>

                <div class="vuln-item medium">
                  <div class="vuln-info">
                    <span class="vuln-label">Medias</span>
                    <span class="vuln-count">{{ totalVulnerabilities.medium }}</span>
                  </div>
                  <mat-progress-bar
                    mode="determinate"
                    [value]="getVulnPercentage('medium')"
                    color="accent"
                  ></mat-progress-bar>
                </div>

                <div class="vuln-item low">
                  <div class="vuln-info">
                    <span class="vuln-label">Bajas</span>
                    <span class="vuln-count">{{ totalVulnerabilities.low }}</span>
                  </div>
                  <mat-progress-bar
                    mode="determinate"
                    [value]="getVulnPercentage('low')"
                  ></mat-progress-bar>
                </div>
              </div>

              <div class="vuln-total">
                <span>Total de Vulnerabilidades</span>
                <strong>{{ getTotalVulnerabilities() }}</strong>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Top Vulnerable Pages -->
          <mat-card class="top-vulnerable-card">
            <mat-card-header>
              <mat-card-title>Páginas con Mayor Riesgo</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="vulnerable-list">
                <div class="vulnerable-item" *ngFor="let page of topVulnerablePages">
                  <div class="vulnerable-info">
                    <div class="vulnerable-header">
                      <span class="vulnerable-name">{{ page.name }}</span>
                      <span class="vulnerable-score" [class]="getScoreClass(page.score)"
                        >{{ page.score }}%</span
                      >
                    </div>
                    <span class="vulnerable-url">{{ page.url }}</span>
                  </div>
                  <div class="vulnerable-vulns">
                    <span class="vuln-tag critical" *ngIf="page.critical"
                      >{{ page.critical }} críticas</span
                    >
                    <span class="vuln-tag high" *ngIf="page.high">{{ page.high }} altas</span>
                  </div>
                </div>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Quick Actions -->
          <mat-card class="actions-card">
            <mat-card-header>
              <mat-card-title>Acciones Rápidas</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="actions-list">
                <button mat-stroked-button class="action-btn" (click)="navigateToPages()">
                  <mat-icon>add_circle</mat-icon>
                  <span>Registrar Nueva Página</span>
                </button>
                <button mat-stroked-button class="action-btn" (click)="navigateToPages()">
                  <mat-icon>play_circle</mat-icon>
                  <span>Iniciar Auditoría Manual</span>
                </button>
                <button mat-stroked-button class="action-btn" (click)="navigateToPages()">
                  <mat-icon>download</mat-icon>
                  <span>Exportar Reporte</span>
                </button>
                <button mat-stroked-button class="action-btn" (click)="navigateToPages()">
                  <mat-icon>schedule</mat-icon>
                  <span>Programar Auditoría</span>
                </button>
              </div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Recent Activity -->
        <mat-card class="activity-card">
          <mat-card-header>
            <mat-card-title>Actividad Reciente</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="activity-list">
              <div class="activity-item" *ngFor="let activity of recentActivity">
                <div class="activity-icon" [ngClass]="activity.type">
                  <mat-icon>{{ activity.icon }}</mat-icon>
                </div>
                <div class="activity-content">
                  <span class="activity-text">{{ activity.text }}</span>
                  <span class="activity-time">{{ activity.time }}</span>
                </div>
              </div>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Upcoming Audits -->
        <mat-card class="upcoming-card">
          <mat-card-header>
            <mat-card-title>Próximas Auditorías Programadas</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="upcoming-list">
              <div class="upcoming-item" *ngFor="let audit of upcomingAudits">
                <div class="upcoming-date">
                  <span class="day">{{ audit.day }}</span>
                  <span class="month">{{ audit.month }}</span>
                </div>
                <div class="upcoming-info">
                  <span class="upcoming-name">{{ audit.pageName }}</span>
                  <span class="upcoming-url">{{ audit.url }}</span>
                </div>
                <div class="upcoming-type" [ngClass]="audit.type">
                  {{ audit.typeLabel }}
                </div>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    </div>
  `,
  styles: [
    `
      .dashboard-container {
        max-width: 1400px;
        margin: 0 auto;
      }

      .page-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 24px;

        h1 {
          font-family: var(--font-display);
          font-size: 28px;
          font-weight: 700;
          letter-spacing: -0.02em;
          color: var(--text-primary);
          margin: 0;
        }

        .subtitle {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 4px 0 0 0;
        }
      }

      .stats-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        gap: 20px;
        margin-bottom: 24px;
      }

      .loading-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        gap: 16px;
        color: var(--text-secondary);

        p {
          margin: 0;
          font-size: 14px;
        }
      }

      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 80px 20px;
        background: white;
        border: 1px solid rgba(0, 0, 0, 0.05);
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-sm);
        margin-bottom: 24px;
        transition:
          transform 0.2s ease,
          box-shadow 0.2s ease;

        &:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        .empty-icon {
          font-size: 56px;
          width: 56px;
          height: 56px;
          color: rgba(0, 0, 0, 0.26);
          margin-bottom: 16px;
        }

        h2 {
          font-family: var(--font-display);
          font-size: 20px;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0 0 8px 0;
        }

        p {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 0 0 20px 0;
          max-width: 360px;
        }
      }

      .stat-card {
        background: white;
        border: 1px solid rgba(0, 0, 0, 0.05);
        border-radius: var(--radius-lg);
        padding: 20px;
        box-shadow: var(--shadow-sm);
        transition:
          transform 0.2s ease,
          box-shadow 0.2s ease;

        &:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }
      }

      .stat-icon {
        width: 48px;
        height: 48px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 16px;

        mat-icon {
          font-size: 24px;
          width: 24px;
          height: 24px;
        }

        &.primary {
          background: rgba(183, 28, 28, 0.1);
          color: var(--primary-color);
        }

        &.success {
          background: rgba(76, 175, 80, 0.1);
          color: #4caf50;
        }

        &.warning {
          background: rgba(255, 152, 0, 0.1);
          color: #ff9800;
        }

        &.error {
          background: rgba(244, 67, 54, 0.1);
          color: #f44336;
        }

        &.info {
          background: rgba(33, 150, 243, 0.1);
          color: #2196f3;
        }

        &.pending {
          background: rgba(183, 28, 28, 0.1);
          color: var(--primary-color);
        }
      }

      .stat-value {
        font-family: var(--font-display);
        font-size: 28px;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--text-primary);
        margin-bottom: 4px;
      }

      .stat-label {
        font-size: 13px;
        color: var(--text-secondary);
      }

      .content-grid {
        display: grid;
        grid-template-columns: 2fr 1fr;
        grid-template-rows: auto auto;
        gap: 24px;

        @media (max-width: 1200px) {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 768px) {
        .stats-grid {
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 12px;
        }

        .stat-card {
          padding: 16px;
        }

        .stat-value {
          font-size: 22px;
        }

        .content-grid {
          gap: 16px;
        }
      }

      .audits-card {
        grid-column: 1;
        grid-row: 1;
      }

      .right-column {
        grid-column: 2;
        grid-row: 1 / 3;
        display: flex;
        flex-direction: column;
        gap: 24px;

        @media (max-width: 1200px) {
          grid-column: 1;
          grid-row: auto;
        }
      }

      .activity-card {
        grid-column: 1;
        grid-row: 2;
      }

      .upcoming-card {
        grid-column: 1;
        grid-row: 3;
      }

      mat-card {
        background: white;
        border: 1px solid rgba(0, 0, 0, 0.05);
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-sm);
        transition:
          transform 0.2s ease,
          box-shadow 0.2s ease;

        &:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }
      }

      mat-card-header {
        margin-bottom: 16px;
      }

      mat-card-title {
        font-family: var(--font-display);
        font-weight: 700;
        color: var(--text-primary);
      }

      .card-subtitle {
        font-size: 12px;
        color: var(--text-secondary);
        margin-left: auto;
      }

      .audits-table {
        width: 100%;
      }

      .page-cell {
        display: flex;
        align-items: center;
        gap: 8px;

        .page-icon {
          font-size: 18px;
          width: 18px;
          height: 18px;
          color: var(--text-secondary);
        }
      }

      .score-cell {
        display: flex;
        align-items: center;
        gap: 12px;

        mat-progress-bar {
          width: 80px;
        }

        span {
          font-weight: 500;
          min-width: 40px;

          &.high {
            color: #2e7d32;
          }
          &.medium {
            color: #f9a825;
          }
          &.low {
            color: #c62828;
          }
        }
      }

      .vuln-mini-badges {
        display: flex;
        gap: 4px;
      }

      .mini-badge {
        padding: 2px 6px;
        border-radius: 999px;
        font-size: 10px;
        font-weight: 600;

        &.critical {
          background: rgba(183, 28, 28, 0.1);
          color: var(--primary-color);
        }

        &.high {
          background: rgba(230, 81, 0, 0.1);
          color: #e65100;
        }

        &.medium {
          background: rgba(249, 168, 37, 0.1);
          color: #f9a825;
        }

        &.low {
          background: rgba(46, 125, 50, 0.1);
          color: #2e7d32;
        }
      }

      .status-badge {
        padding: 4px 12px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 600;

        &.completed {
          background: rgba(76, 175, 80, 0.1);
          color: #2e7d32;
        }

        &.in_progress {
          background: rgba(33, 150, 243, 0.1);
          color: #1565c0;
        }

        &.failed {
          background: rgba(244, 67, 54, 0.1);
          color: #c62828;
        }
      }

      .vuln-list {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .vuln-item {
        .vuln-info {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .vuln-label {
          font-size: 14px;
          color: var(--text-primary);
        }

        .vuln-count {
          font-weight: 500;
        }

        &.critical .vuln-count {
          color: #c62828;
        }
        &.high .vuln-count {
          color: #e65100;
        }
        &.medium .vuln-count {
          color: #f9a825;
        }
        &.low .vuln-count {
          color: #2e7d32;
        }
      }

      .vuln-total {
        display: flex;
        justify-content: space-between;
        padding-top: 16px;
        margin-top: 16px;
        border-top: 1px solid rgba(0, 0, 0, 0.12);
        font-size: 14px;

        strong {
          font-size: 18px;
          color: var(--primary-color);
        }
      }

      .top-vulnerable-card .vulnerable-list {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .vulnerable-item {
        padding-bottom: 16px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);

        &:last-child {
          padding-bottom: 0;
          border-bottom: none;
        }
      }

      .vulnerable-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .vulnerable-name {
        font-weight: 500;
        color: var(--text-primary);
      }

      .vulnerable-score {
        font-weight: 600;
        font-size: 14px;

        &.high {
          color: #2e7d32;
        }
        &.medium {
          color: #f9a825;
        }
        &.low {
          color: #c62828;
        }
      }

      .vulnerable-url {
        font-size: 12px;
        color: var(--text-secondary);
        display: block;
        margin-top: 2px;
      }

      .vulnerable-vulns {
        display: flex;
        gap: 8px;
        margin-top: 8px;
      }

      .vuln-tag {
        font-size: 11px;
        padding: 2px 8px;
        border-radius: 999px;
        font-weight: 600;

        &.critical {
          background: rgba(183, 28, 28, 0.1);
          color: var(--primary-color);
        }

        &.high {
          background: rgba(230, 81, 0, 0.1);
          color: #e65100;
        }
      }

      .actions-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .action-btn {
        justify-content: flex-start;
        padding: 12px 16px;

        mat-icon {
          margin-right: 12px;
          color: var(--primary-color);
        }
      }

      .activity-list {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .activity-item {
        display: flex;
        gap: 12px;
        align-items: flex-start;
      }

      .activity-icon {
        width: 36px;
        height: 36px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        mat-icon {
          font-size: 18px;
          width: 18px;
          height: 18px;
        }

        &.audit {
          background: rgba(183, 28, 28, 0.1);
          color: var(--primary-color);
        }

        &.vulnerability {
          background: rgba(244, 67, 54, 0.1);
          color: #f44336;
        }

        &.user {
          background: rgba(183, 28, 28, 0.1);
          color: var(--primary-color);
        }

        &.page {
          background: rgba(76, 175, 80, 0.1);
          color: #4caf50;
        }

        &.fix {
          background: rgba(33, 150, 243, 0.1);
          color: #2196f3;
        }
      }

      .activity-content {
        display: flex;
        flex-direction: column;
        flex: 1;
      }

      .activity-text {
        font-size: 13px;
        color: var(--text-primary);
      }

      .activity-time {
        font-size: 12px;
        color: var(--text-secondary);
        margin-top: 2px;
      }

      .upcoming-list {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .upcoming-item {
        display: flex;
        gap: 16px;
        align-items: center;
      }

      .upcoming-date {
        width: 50px;
        height: 50px;
        background: rgba(183, 28, 28, 0.1);
        border-radius: var(--radius-sm);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        .day {
          font-size: 18px;
          font-weight: 600;
          color: var(--primary-color);
          line-height: 1;
        }

        .month {
          font-size: 11px;
          color: var(--primary-color);
          text-transform: uppercase;
        }
      }

      .upcoming-info {
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      .upcoming-name {
        font-weight: 500;
        color: var(--text-primary);
      }

      .upcoming-url {
        font-size: 12px;
        color: var(--text-secondary);
      }

      .upcoming-type {
        padding: 4px 12px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 600;

        &.automatic {
          background: rgba(33, 150, 243, 0.1);
          color: #1565c0;
        }

        &.manual {
          background: rgba(183, 28, 28, 0.1);
          color: var(--primary-color);
        }
      }
    `,
  ],
})
export class DashboardComponent implements OnInit, OnDestroy {
  loading = false;
  private pollSubscription?: Subscription;

  stats = {
    totalUsers: 0,
    activeUsers: 0,
    totalPages: 0,
    pagesWithIssues: 0,
    pendingAudits: 0,
    avgSecurityScore: 0,
    criticalVulnerabilities: 0,
    recentAudits: [] as AuditResult[],
  };

  totalVulnerabilities = {
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
  };

  recentAudits: any[] = [];

  topVulnerablePages: any[] = [];

  recentActivity: any[] = [];

  upcomingAudits: any[] = [];

  auditColumns = ['pageName', 'date', 'score', 'vulnerabilities', 'status'];

  private pages: WebPage[] = [];

  constructor(
    private webPageService: WebPageService,
    private pageProcessService: PageProcessService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadDashboard();
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  loadDashboard() {
    this.loading = true;
    this.webPageService.getMyPages().subscribe({
      next: (pages) => {
        this.pages = pages;
        this.calculateStats();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.showErrorWithRetry('Error al cargar el dashboard', () => this.loadDashboard());
      }
    });
  }

  refresh() {
    this.loadDashboard();
  }

  private showErrorWithRetry(message: string, retryAction: () => void) {
    this.snackBar.open(message, 'Reintentar', { duration: 6000 })
      .onAction()
      .subscribe(() => retryAction());
  }

  private stopPolling() {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = undefined;
    }
  }

  private startPollingIfNeeded() {
    if (this.stats.pendingAudits > 0) {
      if (!this.pollSubscription) {
        this.pollSubscription = interval(5000).subscribe(() => {
          this.webPageService.getMyPages().subscribe({
            next: (pages) => {
              this.pages = pages;
              this.calculateStats();
            },
            error: () => {
              this.stopPolling();
            }
          });
        });
      }
    } else {
      this.stopPolling();
    }
  }

  private calculateStats() {
    const pages = this.pages;

    this.stats.totalPages = pages.length;
    this.stats.pendingAudits = 0;
    this.stats.pagesWithIssues = 0;
    this.stats.criticalVulnerabilities = 0;

    this.totalVulnerabilities = { critical: 0, high: 0, medium: 0, low: 0 };

    let scoreSum = 0;
    let scoredPages = 0;
    const pageRows: any[] = [];

    for (const page of pages) {
      const vulns = page.vulnerabilities || { critical: 0, high: 0, medium: 0, low: 0 };

      this.totalVulnerabilities.critical += vulns.critical;
      this.totalVulnerabilities.high += vulns.high;
      this.totalVulnerabilities.medium += vulns.medium;
      this.totalVulnerabilities.low += vulns.low;
      this.stats.criticalVulnerabilities += vulns.critical;

      if (page.securityScore !== undefined && page.securityScore !== null) {
        scoreSum += page.securityScore;
        scoredPages++;
      }

      const totalForPage = vulns.critical + vulns.high + vulns.medium + vulns.low;
      if (totalForPage > 0) {
        this.stats.pagesWithIssues++;
      }

      pageRows.push({
        name: page.name,
        url: page.url,
        score: page.securityScore ?? 0,
        critical: vulns.critical,
        high: vulns.high
      });
    }

    this.stats.avgSecurityScore = scoredPages > 0 ? Math.round(scoreSum / scoredPages) : 0;

    this.topVulnerablePages = pageRows
      .filter(p => p.critical > 0 || p.high > 0)
      .sort((a, b) => a.score - b.score)
      .slice(0, 5);

    this.loadRecentAudits();
  }

  private loadRecentAudits() {
    const pageIds = this.pages.map(p => p.id);
    if (pageIds.length === 0) {
      this.recentAudits = [];
      this.stats.pendingAudits = 0;
      this.recentActivity = [];
      this.upcomingAudits = [];
      this.startPollingIfNeeded();
      return;
    }

    const allAudits: any[] = [];
    let pending = 0;
    let completed = 0;
    const activity: any[] = [];

    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    this.pageProcessService.getProcessesByMonth(currentMonth, currentYear).subscribe({
      next: (processes) => {
        pending = processes.filter(p => p.status === 0 || p.status === 1 || p.status === 2).length;
        this.stats.pendingAudits = pending;

        for (const process of processes) {
          const page = this.pages.find(p => p.id === process.pageId);
          const name = page?.name || process.pageName;

          activity.push({
            type: 'audit',
            icon: 'assignment',
            text: `Auditoría ${this.processStatusLabel(process.status)} en ${name}`,
            time: process.updatedAt ? this.timeAgo(process.updatedAt) : '—'
          });

          if (process.status === 3 && process.result?.overall_score !== undefined) {
            const score = process.result.overall_score;
            const checks = process.result.checks || {};
            const vulnCounts = this.countVulnsFromChecks(checks);

            this.totalVulnerabilities.critical += vulnCounts.critical;
            this.totalVulnerabilities.high += vulnCounts.high;
            this.totalVulnerabilities.medium += vulnCounts.medium;
            this.totalVulnerabilities.low += vulnCounts.low;

            allAudits.push({
              pageName: name,
              date: process.updatedAt,
              score,
              status: 'completed',
              vulnCritical: vulnCounts.critical,
              vulnHigh: vulnCounts.high,
              vulnMedium: vulnCounts.medium,
              vulnLow: vulnCounts.low
            });
            completed++;
          }
        }

        allAudits.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        this.recentAudits = allAudits.slice(0, 5);
        this.recentActivity = activity.slice(0, 5);

        this.upcomingAudits = processes
          .filter(p => p.status === 0)
          .slice(0, 4)
          .map(p => {
            const page = this.pages.find(pg => pg.id === p.pageId);
            return {
              day: new Date(p.updatedAt || today).getDate().toString().padStart(2, '0'),
              month: this.monthShort((p.mes || today.getMonth() + 1) - 1),
              pageName: page?.name || p.pageName,
              url: page?.url || '',
              type: p.processType === 1 ? 'manual' : 'automatic',
              typeLabel: p.processType === 1 ? 'Manual' : 'Automática'
            };
          });

        this.startPollingIfNeeded();
      },
      error: () => {
        this.recentAudits = [];
        this.recentActivity = [];
        this.upcomingAudits = [];
      }
    });
  }

  private countVulnsFromChecks(checks: Record<string, any>): { critical: number; high: number; medium: number; low: number } {
    const counts = { critical: 0, high: 0, medium: 0, low: 0 };
    if (!checks) return counts;

    for (const key of Object.keys(checks)) {
      const check = checks[key];
      if (!check) continue;
      const severity = (check.severity || '').toLowerCase();
      if (severity === 'critical') counts.critical++;
      else if (severity === 'high') counts.high++;
      else if (severity === 'medium') counts.medium++;
      else if (severity === 'low') counts.low++;
    }
    return counts;
  }

  private processStatusLabel(status: number): string {
    const labels: Record<number, string> = {
      0: 'pendiente',
      1: 'encolada',
      2: 'en progreso',
      3: 'completada',
      4: 'fallida',
      5: 'cancelada'
    };
    return labels[status] || 'desconocida';
  }

  private timeAgo(dateValue: Date): string {
    const date = new Date(dateValue);
    const diffMs = Date.now() - date.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Ahora';
    if (mins < 60) return `Hace ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Hace ${hours} h`;
    const days = Math.floor(hours / 24);
    return `Hace ${days} d`;
  }

  private monthShort(index: number): string {
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return months[index] || '';
  }

  navigateToPages() {
    this.router.navigate(['/pages']);
  }

  getScoreColor(score: number): 'primary' | 'accent' | 'warn' {
    if (score >= 80) return 'primary';
    if (score >= 60) return 'accent';
    return 'warn';
  }

  getScoreClass(score: number): string {
    if (score >= 80) return 'high';
    if (score >= 60) return 'medium';
    return 'low';
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      completed: 'Completada',
      in_progress: 'En Progreso',
      failed: 'Fallida',
    };
    return labels[status] || status;
  }

  getVulnPercentage(severity: 'critical' | 'high' | 'medium' | 'low'): number {
    const total = this.getTotalVulnerabilities();
    return total > 0 ? (this.totalVulnerabilities[severity] / total) * 100 : 0;
  }

  getTotalVulnerabilities(): number {
    return (
      this.totalVulnerabilities.critical +
      this.totalVulnerabilities.high +
      this.totalVulnerabilities.medium +
      this.totalVulnerabilities.low
    );
  }
}
